import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { assignmentAPI, videoAPI, projectAPI, translatorAPI } from '@/utils/api';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { ClipboardList, Plus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const AdminAssignments = () => {
  const { user } = useOutletContext();
  const [assignments, setAssignments] = useState([]);
  const [translators, setTranslators] = useState([]);
  const [projects, setProjects] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  
  const [form, setForm] = useState({
    translator_id: '',
    project_id: '',
    video_id: '',
    language: '',
    task_type: 'translation',
    notes: ''
  });
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [assignmentsRes, translatorsRes, projectsRes] = await Promise.all([
        assignmentAPI.list({}),
        translatorAPI.listTranslators(),
        projectAPI.list()
      ]);
      setAssignments(assignmentsRes.data);
      setTranslators(translatorsRes.data.filter(t => t.nda_signed));
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadVideos = async (projectId) => {
    try {
      const response = await videoAPI.list(projectId);
      setVideos(response.data.filter(v => v.status === 'pending'));
    } catch (error) {
      console.error('Error loading videos:', error);
    }
  };
  
  const handleProjectChange = (projectId) => {
    setForm({...form, project_id: projectId, video_id: ''});
    loadVideos(projectId);
  };
  
  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      await assignmentAPI.assign(form);
      toast.success('Video assigned successfully!');
      setShowDialog(false);
      setForm({
        translator_id: '',
        project_id: '',
        video_id: '',
        language: '',
        task_type: 'translation',
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign video');
    }
  };
  
  const getStatusColor = (status) => {
    const colors = {
      assigned: 'bg-accent/10 text-accent',
      in_progress: 'bg-creative/10 text-creative',
      submitted: 'bg-warning/10 text-warning',
      approved: 'bg-success/10 text-success',
      invoiced: 'bg-primary/10 text-primary',
      paid: 'bg-success/10 text-success',
    };
    return colors[status] || 'bg-secondary text-secondary-foreground';
  };
  
  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-primary mb-2">Manage Assignments</h1>
            <p className="text-muted-foreground">Assign videos to translators and track progress</p>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button size="lg" data-testid="btn-create-assignment">
                <Plus className="w-5 h-5 mr-2" />
                Assign Video
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Assign Video to Translator</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAssign} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="translator">Translator * (Only NDA signed)</Label>
                  <Select value={form.translator_id} onValueChange={(value) => setForm({...form, translator_id: value})} required>
                    <SelectTrigger data-testid="select-translator">
                      <SelectValue placeholder="Select translator" />
                    </SelectTrigger>
                    <SelectContent>
                      {translators.map((translator) => (
                        <SelectItem key={translator.user_id} value={translator.user_id}>
                          {translator.full_name} ({translator.personal_email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {translators.length === 0 && (
                    <p className="text-sm text-warning">No translators with signed NDA available</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="project">Project *</Label>
                  <Select value={form.project_id} onValueChange={handleProjectChange} required>
                    <SelectTrigger data-testid="select-project">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.project_id} value={project.project_id}>
                          {project.client_name} - {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="video">Video *</Label>
                  <Select value={form.video_id} onValueChange={(value) => setForm({...form, video_id: value})} required disabled={!form.project_id}>
                    <SelectTrigger data-testid="select-video">
                      <SelectValue placeholder="Select video" />
                    </SelectTrigger>
                    <SelectContent>
                      {videos.map((video) => (
                        <SelectItem key={video.video_id} value={video.video_id}>
                          {video.title} ({video.duration_minutes} min)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.project_id && videos.length === 0 && (
                    <p className="text-sm text-warning">No unassigned videos available in this project</p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="language">Language *</Label>
                    <Input
                      id="language"
                      value={form.language}
                      onChange={(e) => setForm({...form, language: e.target.value})}
                      placeholder="e.g., Hindi, Spanish"
                      required
                      data-testid="input-language"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="task_type">Task Type *</Label>
                    <Select value={form.task_type} onValueChange={(value) => setForm({...form, task_type: value})} required>
                      <SelectTrigger data-testid="select-task-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="translation">Translation</SelectItem>
                        <SelectItem value="qc">QC (Quality Check)</SelectItem>
                        <SelectItem value="template">Template</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Input
                    id="notes"
                    value={form.notes}
                    onChange={(e) => setForm({...form, notes: e.target.value})}
                    placeholder="Any special instructions"
                    data-testid="input-notes"
                  />
                </div>
                
                <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">
                    <strong>Note:</strong> Rate will be automatically determined based on project and task type configuration.
                    Email notification will be logged (mock) to the translator.
                  </p>
                </div>
                
                <Button type="submit" className="w-full" size="lg" data-testid="btn-save-assignment">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign Video
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-accent" />
              All Assignments ({assignments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Loading assignments...</p>
              </div>
            ) : assignments.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No assignments yet. Assign your first video to a translator.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {assignments.map((assignment) => (
                  <Card 
                    key={assignment.assignment_id}
                    className="hover:shadow-md transition-shadow"
                    data-testid={`assignment-${assignment.assignment_id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-primary">{assignment.video_title}</h3>
                            <Badge className={getStatusColor(assignment.status)}>
                              {assignment.status.replace('_', ' ')}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                            <div>
                              <p className="text-muted-foreground">Client</p>
                              <p className="font-medium text-primary">{assignment.client_name}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Project</p>
                              <p className="font-medium text-primary">{assignment.project_name}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Language</p>
                              <p className="font-medium text-primary">{assignment.language}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Task Type</p>
                              <p className="font-medium text-primary capitalize">{assignment.task_type}</p>
                            </div>
                          </div>
                          
                          {assignment.notes && (
                            <p className="text-sm text-muted-foreground italic mb-2">Note: {assignment.notes}</p>
                          )}
                          
                          <p className="text-xs text-muted-foreground">Assigned: {formatDate(assignment.assigned_at)}</p>
                        </div>
                        
                        <div className="text-right space-y-2">
                          <div>
                            <p className="text-sm text-muted-foreground">Duration</p>
                            <p className="text-xl font-bold text-primary">{assignment.video_duration} min</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Rate</p>
                            <p className="text-lg font-semibold text-accent">{formatCurrency(assignment.rate_per_minute)}/min</p>
                          </div>
                          <div className="pt-2 border-t border-border">
                            <p className="text-sm text-muted-foreground">Total</p>
                            <p className="text-xl font-bold text-success">
                              {formatCurrency((assignment.minutes_worked || assignment.video_duration) * assignment.rate_per_minute)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminAssignments;
